import React, { useCallback, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Help as HelpIcon,
  Videocam as VideocamIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { useTranslation } from 'react-i18next';
import DynamicDeskLogo from "../assets/DD_2_Weiss.png";
import UserAccountPopover from '../components/layout/UserAccountPopover';
import { useAuthStore } from '../stores/zustand';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../services';
import { useNavigate } from 'react-router-dom';

const DashboardPhysioNavbar = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const { user, preferredLanguage, setPreferredLanguage, clearAuth } = useAuthStore();

  // same video from PhysioVideoSelection
  const videoUrl = i18n.language === "en"
    ? "https://vimeo.com/1031253750"
    : "https://vimeo.com/1059501770";

  // Video handlers
  const handleOpenVideoDialog = () => setIsVideoDialogOpen(true);
  const handleCloseVideoDialog = () => setIsVideoDialogOpen(false);

  // Profile handlers
  const profileOpen = Boolean(anchorEl);
  const initials = user.name
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "DU";

  const handleProfileClick = (event) => setAnchorEl(event.currentTarget);
  const handleProfileClose = () => setAnchorEl(null);

  // Language switch handlers
  const handleLanguageClick = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    // Update i18next
    i18n.changeLanguage(lang);

    // Update auth store
    setPreferredLanguage(lang);

    // Close menu
    handleLanguageClose();
  };

  const handleLogout = useCallback(async () => {
      try {
        await authApi.logout();
      } catch (error) {
        console.error("Logout API call failed:", error);
      }
  
      // Clear TanStack Query cache
      queryClient.clear();
  
      // Clear AuthStore
      clearAuth();
  
      if (window.electronAPI?.navigation) {
        console.log("[MainContainer] Using Electron navigation for logout");
        try {
          await window.electronAPI.navigation.navigateToAuthenticatedState(false);
        } catch (navError) {
          console.warn("[MainContainer] Electron navigation failed:", navError);
          navigate("/");
        }
      } else {
        navigate("/");
      }
    }, [queryClient, clearAuth, navigate]);

  const rightNavItems = [
    {
      icon: <VideocamIcon />,
      label: t("videoLibrary", "Video Library"),
      onClick: () => navigate("/physio-video-selection"),
      key: "videoLibrary"
    },
    {
      icon: <HelpIcon />,
      label: t("videoTutorial", "Video Tutorial"),
      onClick: handleOpenVideoDialog,
      key: "video"
    },
    // { icon: <SettingsIcon />, label: t("settings", "Einstellungen"), key: "settings" },
  ];

  return (
    <>
      <AppBar position="sticky" sx={{ background: "#1a2332", boxShadow: "none" }}>
        <Toolbar sx={{ justifyContent: "space-between", py: 1.5, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <img
              src={DynamicDeskLogo}
              style={{ width: "auto", height: "50px" }}
              alt={t("logoAlt", "DynamicDesk Logo")}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>

            {/* Navigation Icons */}
            {rightNavItems.map((item) => (
              <IconButton
                key={item.key}
                onClick={item.onClick}
                sx={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "#2d3748", color: "white",
                  "&:hover": { background: "#3d4a5c" },
                }}
                title={item.label}
              >
                {item.icon}
              </IconButton>
            ))}

            {/* Profile Avatar */}
            <IconButton
              onClick={handleProfileClick}
              sx={{
                width: 48, height: 48, borderRadius: "50%",
                background: "#2d3748", color: "white",
                "&:hover": { background: "#3d4a5c" },
              }}
              title={t("account", "Account")}
            >
              <Avatar sx={{
                width: 32, height: 32, fontSize: "0.75rem",
                fontWeight: 700, bgcolor: "#2563EB",
              }}>
                {initials}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Language Dropdown Menu */}
      <Menu
        anchorEl={languageAnchorEl}
        open={Boolean(languageAnchorEl)}
        onClose={handleLanguageClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ mt: 1 }}
        PaperProps={{
          sx: {
            minWidth: 120,
            bgcolor: '#2d3748',
            '& .MuiMenuItem-root': {
              color: 'white',
              '&:hover': {
                backgroundColor: '#3d4a5c',
              },
            },
          },
        }}
      >
        <MenuItem
          onClick={() => handleLanguageChange('de')}
          selected={i18n.language === 'de'}
          sx={{ justifyContent: 'space-between' }}
        >
          🇩🇪 {t('german', 'Deutsch')}
        </MenuItem>
        <MenuItem
          onClick={() => handleLanguageChange('en')}
          selected={i18n.language === 'en'}
          sx={{ justifyContent: 'space-between' }}
        >
          🇺🇸 {t('english', 'English')}
        </MenuItem>
      </Menu>

      {/* UserAccountPopover */}
      <UserAccountPopover
        open={profileOpen}
        anchorEl={anchorEl}
        onClose={handleProfileClose}
        onLanguageToggle={() => handleLanguageChange(i18n.language === 'de' ? 'en' : 'de')}
        onPasswordUpdate={async () => ({ success: true, message: t("passwordUpdated") })}
        onLogout={handleLogout}
        onTestNotifications={() => { }}
        currentLanguage={i18n.language}
        userEmail={user.email}
        notificationPermission="granted"
        onEnableNotifications={() => { }}
        isElectron={false}
        isCheckoutPage={false}
      />

      {/* Video Dialog */}
      <Dialog open={isVideoDialogOpen} onClose={handleCloseVideoDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1a2332", color: "white" }}>
          <IconButton
            onClick={handleCloseVideoDialog}
            sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6">
            {t("tutorialVideo", "Tutorial Video")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: "#00003e" }}>
          <ReactPlayer
            url={videoUrl}
            controls
            width="100%"
            height="500px"
            playing={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DashboardPhysioNavbar;